'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { CallToAction, CloseContainer } from '@benzinga/ui';
import { selectCampaignTargets } from '../../utils/targeting';
import { isMobile } from '@benzinga/device-utils';
import { WordpressWidget } from '@benzinga/content-manager';
import { MoneyBlocks } from '../MoneyBlocks';
import { Impression, useGeo } from '@benzinga/analytics';

export interface CampaignPopupProps {
  campaign?: any;
  targets: any[];
}

export const CampaignPopup: React.FC<CampaignPopupProps> = ({ campaign, targets }) => {
  const [currentCampaign, setCurrentCampaign] = React.useState(campaign);
  const [currentWidget, setCurrentWidget] = React.useState<WordpressWidget>();
  const [isVisible, setIsVisible] = React.useState(false);
  const [didDismiss, setDidDismiss] = React.useState(false);
  const geo = useGeo();

  const handleOnClose = React.useCallback(() => {
    setIsVisible(false);
    setDidDismiss(true);
  }, []);

  React.useEffect(() => {
    const handleExitIntent = () => {
      setIsVisible(false);
      setDidDismiss(true);
    };
    window.addEventListener('keydown', handleExitIntent);
    return () => {
      window.removeEventListener('keydown', handleExitIntent);
    };
  }, [isVisible, didDismiss]);

  React.useEffect(() => {
    const handleExitIntent = event => {
      if (!event.toElement && !event.relatedTarget) {
        if (!isVisible && !didDismiss) {
          setIsVisible(true);
        }
      }
    };
    document.addEventListener('mouseout', handleExitIntent);
    return () => {
      window.removeEventListener('mouseout', handleExitIntent);
    };
  }, [isVisible, didDismiss]);

  React.useEffect(() => {
    if (geo === null) return;
    const options = selectCampaignTargets(targets, geo, isMobile());
    const placement = options['popup'];
    if (placement?.type === 'widget') {
      setCurrentWidget(placement);
    } else {
      setCurrentCampaign(placement?.campaign);
    }
  }, [geo, targets]);

  // If no campaign
  if (!currentCampaign && !currentWidget) {
    return <div />;
  }

  if (currentWidget && (currentWidget?.blocks?.length ?? 0) < 1) {
    return <div />;
  }

  let popupClassName = isVisible && !didDismiss ? 'visible' : '';
  if (currentWidget) {
    popupClassName += ' widget';
  }

  let popupAction = 'Show Popup';
  if (currentCampaign?.cta_action) {
    popupAction = currentCampaign.cta_action;
  } else if (currentWidget?.name) {
    popupAction = currentWidget.name;
  }

  return (
    <PopupWrapper className={popupClassName}>
      <Impression
        campaign_id={currentCampaign?.id ?? popupAction}
        tag={currentCampaign?.impression_tag}
        unit_type="Popup CTA"
      >
        <CloseContainer onClose={handleOnClose}>
          {currentWidget?.blocks && <MoneyBlocks blocks={currentWidget.blocks} className="p-4" />}
          {currentCampaign && (
            <CallToAction
              button_text={currentCampaign.cta_text}
              description={currentCampaign.cta_description}
              disclosure={currentCampaign.disclosure}
              go_link_dofollow={currentCampaign?.go_link_dofollow}
              id={currentCampaign.id}
              image_url={currentCampaign.image_url}
              layout="popup"
              onClick={handleOnClose}
              show_bz_badge={true}
              title={currentCampaign.cta_title}
              url={currentCampaign.url}
              utm_action={currentCampaign.utm_action ? currentCampaign.utm_action : currentCampaign.cta_title}
              utm_category="Lead Click"
              utm_label="Popup CTA"
            />
          )}
        </CloseContainer>
      </Impression>
    </PopupWrapper>
  );
};

const PopupWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 1rem;
  display: none;
  overflow-y: scroll;
  z-index: 50;

  .call-to-action-container {
    border: none;
    box-shadow: none;
  }

  &.widget {
    .close-container {
      max-width: 750px;
    }
  }

  .close-container {
    position: relative;
    top: 20vh;
    max-width: 600px;
    margin: auto;
    background: white;
  }
  .call-to-action-wrapper {
    padding: 1rem;
  }
  &.visible {
    display: block;
  }
`;
